import { X, ShieldCheck, CreditCard, Lock, Sparkles, Zap, AlertCircle } from 'lucide-react';

function PaywallModal({
  open,
  onClose,
  onPay,
  isPaying,
  creditsUsed,
  freeLimit,
  paidCredits = 0,
  paidLimit = 20,
  priceDisplay,
  error
}) {
  if (!open) return null;

  const totalCredits = freeLimit + paidCredits;
  const remaining = Math.max(totalCredits - creditsUsed, 0);
  const isOutOfCredits = remaining === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm px-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <Sparkles size={22} className="text-slate-600" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-900 m-0">
                {isOutOfCredits ? 'Out of Credits' : 'Get More Credits'}
              </h2>
              <p className="text-sm text-slate-500">
                {isOutOfCredits
                  ? 'Buy credits to continue tailoring resumes'
                  : `You have ${remaining} credit${remaining === 1 ? '' : 's'}`
                }
              </p>
            </div>
          </div>

          {/* What you get */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-heading text-3xl font-bold text-slate-900">{paidLimit}</p>
                <p className="text-sm text-slate-600">credits</p>
              </div>
              <div className="text-right">
                <p className="font-heading text-3xl font-bold text-primary">{priceDisplay}</p>
                <p className="text-sm text-slate-500">one-time</p>
              </div>
            </div>
          </div>

          {/* What uses credits */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <Zap size={16} className="text-slate-600" />
              1 credit per action
            </p>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• Tailor a resume for a job</li>
              <li>• Generate interview prep</li>
              <li>• Analyze a job description</li>
            </ul>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-green-600" />
              Secure checkout
            </span>
            <span className="flex items-center gap-1.5">
              <CreditCard size={14} className="text-primary" />
              Card, Apple Pay, Google Pay
            </span>
            <span className="flex items-center gap-1.5">
              <Lock size={14} />
              Instant delivery
            </span>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle size={16} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium transition-colors cursor-pointer"
            >
              {isOutOfCredits ? 'Cancel' : 'Not now'}
            </button>
            <button
              onClick={onPay}
              disabled={isPaying}
              className={`w-full sm:w-auto px-5 py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                isPaying ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-dark cursor-pointer'
              }`}
            >
              {isPaying ? 'Redirecting...' : `Buy ${paidLimit} credits`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaywallModal;
